import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Globe, AlertTriangle, CheckCircle2, AlertCircle, Download, Upload, Settings, Save, X, Search, MinusCircle, ChevronLeft, ChevronRight, TrendingDown, Package, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  { code: 'UAE', name: 'UAE', flag: '🇦🇪', platform: 'amazon_uae' },
  { code: 'KSA', name: 'KSA', flag: '🇸🇦', platform: 'amazon_ksa' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', platform: 'amazon_au' },
  { code: 'NOON_UAE', name: 'Noon UAE', flag: '🛒', platform: 'noon_uae' },
];

export default function FBAForecastPage() {
  const [selectedCountry, setSelectedCountry] = useState('UAE');
  const [forecasts, setForecasts] = useState([]);
  const [forecastInfo, setForecastInfo] = useState(null);
  const [warehouseSettings, setWarehouseSettings] = useState([]);
  const [stockUpdateDates, setStockUpdateDates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingStock, setEditingStock] = useState({});
  const [savingStock, setSavingStock] = useState({});
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const currentPlatform = countries.find(c => c.code === selectedCountry)?.platform;

  // Filter forecasts based on search term
  const filteredForecasts = useMemo(() => {
    if (!searchTerm.trim()) return forecasts;
    const search = searchTerm.toLowerCase();
    return forecasts.filter(f => 
      f.sku.toLowerCase().includes(search) || 
      f.product_name.toLowerCase().includes(search)
    );
  }, [forecasts, searchTerm]);

  // Paginate filtered forecasts
  const paginatedForecasts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredForecasts.slice(startIndex, startIndex + pageSize);
  }, [filteredForecasts, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredForecasts.length / pageSize);

  // Reset to page 1 when search or country changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCountry]);

  useEffect(() => {
    fetchForecast();
    fetchForecastInfo();
    fetchWarehouseSettings();
    fetchStockUpdateDates();
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStockUpdateDates = async () => {
    try {
      const response = await axios.get(`${API}/stock-update-dates`);
      setStockUpdateDates(response.data);
    } catch (error) {
      console.error('Failed to load stock update dates:', error);
    }
  };

  const fetchForecast = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/forecast/fba/${selectedCountry}`);
      setForecasts(response.data);
      // Initialize editing state
      const editState = {};
      response.data.forEach(f => {
        editState[f.sku] = {
          warehouse_stock: f.warehouse_stock,
          stock_in_transit: f.stock_in_transit
        };
      });
      setEditingStock(editState);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load FBA forecast',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchForecastInfo = async () => {
    try {
      const response = await axios.get(`${API}/forecast/fba/${selectedCountry}/info`);
      setForecastInfo(response.data);
    } catch (error) {
      console.error('Failed to load forecast info:', error);
    }
  };

  const fetchWarehouseSettings = async () => {
    try {
      const response = await axios.get(`${API}/warehouse-settings`);
      setWarehouseSettings(response.data);
    } catch (error) {
      console.error('Failed to load warehouse settings:', error);
    }
  };

  const getCurrentSettings = () => {
    return warehouseSettings.find(s => s.platform === currentPlatform) || {
      shipping_lead_time_days: 14,
      target_days_of_supply: 30
    };
  };

  const handleStockChange = (sku, field, value) => {
    setEditingStock(prev => ({
      ...prev,
      [sku]: {
        ...prev[sku],
        [field]: parseInt(value) || 0
      }
    }));
  };

  const saveStock = async (sku) => {
    setSavingStock(prev => ({ ...prev, [sku]: true }));
    try {
      await axios.put(`${API}/warehouse-stock/${currentPlatform}`, {
        sku: sku,
        current_stock: editingStock[sku]?.warehouse_stock || 0,
        stock_in_transit: editingStock[sku]?.stock_in_transit || 0
      });
      toast({ title: 'Success', description: `Stock updated for ${sku}` });
      fetchForecast(); // Refresh to get new calculations
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update stock',
        variant: 'destructive',
      });
    } finally {
      setSavingStock(prev => ({ ...prev, [sku]: false }));
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/warehouse-stock/${currentPlatform}/bulk-import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const mappedMsg = response.data.mapped_to_master > 0 
        ? ` (${response.data.mapped_to_master} SKUs mapped to master)` 
        : '';
      toast({
        title: 'Upload Complete',
        description: `${response.data.created} created, ${response.data.updated} updated${mappedMsg}`,
      });
      setBulkUploadOpen(false);
      fetchForecast();
      fetchStockUpdateDates();
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload file',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/warehouse-stock/${currentPlatform}/download-template`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `warehouse_stock_template_${currentPlatform}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download template', variant: 'destructive' });
    }
  };

  const downloadUnmatchedSkus = async () => {
    try {
      const response = await axios.get(`${API}/forecast/fba/${selectedCountry}/unmatched-skus`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `unmatched_skus_${selectedCountry}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Success',
        description: 'Unmatched SKUs list downloaded',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download unmatched SKUs list',
        variant: 'destructive',
      });
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/fba-forecast/${selectedCountry}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fba_forecast_${selectedCountry}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Success',
        description: 'Forecast plan downloaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download forecast plan',
        variant: 'destructive',
      });
    }
  };

  const saveWarehouseSettings = async (newSettings) => {
    try {
      const updatedSettings = warehouseSettings.map(s => 
        s.platform === currentPlatform ? { ...s, ...newSettings } : s
      );
      
      // If platform doesn't exist in settings, add it
      if (!warehouseSettings.find(s => s.platform === currentPlatform)) {
        updatedSettings.push({
          platform: currentPlatform,
          shipping_lead_time_days: newSettings.shipping_lead_time_days || 14,
          target_days_of_supply: newSettings.target_days_of_supply || 30
        });
      }

      await axios.post(`${API}/warehouse-settings`, updatedSettings);
      toast({ title: 'Success', description: 'Settings saved' });
      setSettingsOpen(false);
      fetchWarehouseSettings();
      fetchForecast();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status, hasShortage) => {
    if (hasShortage) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-orange-500/10 text-orange-600 text-xs font-medium" data-testid="status-shortage">
          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
          SHORTAGE
        </span>
      );
    }
    if (status === 'urgent') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-destructive/10 text-destructive text-xs font-medium" data-testid={`status-${status}`}>
          <AlertCircle className="w-3 h-3" strokeWidth={2} />
          URGENT
        </span>
      );
    }
    if (status === 'warning') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-warning/10 text-warning text-xs font-medium" data-testid={`status-${status}`}>
          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
          WARNING
        </span>
      );
    }
    if (status === 'no_data') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-muted text-muted-foreground text-xs font-medium" data-testid={`status-${status}`}>
          <MinusCircle className="w-3 h-3" strokeWidth={2} />
          NO SALES
        </span>
      );
    }
    if (status === 'overstock') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-blue-500/10 text-blue-600 text-xs font-medium" data-testid={`status-${status}`}>
          <Package className="w-3 h-3" strokeWidth={2} />
          OVERSTOCK
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-success/10 text-success text-xs font-medium" data-testid={`status-${status}`}>
        <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
        OK
      </span>
    );
  };

  const currentSettings = getCurrentSettings();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">FBA Inventory Forecast</h1>
            <p className="text-muted-foreground mt-2">
              Formula: Order Qty = Velocity × (Lead Time + Target Days) - Warehouse Stock - In Transit
            </p>
          </div>
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="settings-button">
            <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Settings
          </Button>
        </div>

        {/* Country Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => setSelectedCountry(country.code)}
                data-testid={`country-tab-${country.code}`}
                className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors border ${
                  selectedCountry === country.code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <span className="mr-2">{country.flag}</span>
                {country.name}
              </button>
            ))}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkUploadOpen(true)} data-testid="bulk-upload-button">
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Update Stock
            </Button>
            {forecasts.length > 0 && (
              <Button onClick={handleExport} data-testid="export-forecast-button">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Export Plan
              </Button>
            )}
          </div>
        </div>

        {/* Search Bar and Pagination Controls */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <Input
              data-testid="forecast-search-input"
              placeholder="Search by SKU or product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-8" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-muted-foreground">
              {filteredForecasts.length > 0 ? (
                <>Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredForecasts.length)} of {filteredForecasts.length}</>
              ) : (
                <>0 products</>
              )}
            </span>
          </div>
        </div>

        {/* Settings Info Bar */}
        <div className="bg-muted/30 border border-border rounded-sm p-3 flex items-center justify-between">
          <div className="flex gap-6 text-sm">
            <span>
              <span className="text-muted-foreground">Lead Time:</span>{' '}
              <strong>{currentSettings.shipping_lead_time_days} days</strong>
            </span>
            <span>
              <span className="text-muted-foreground">Target Supply:</span>{' '}
              <strong>{currentSettings.target_days_of_supply || 30} days</strong>
            </span>
          </div>
          {forecastInfo?.has_data && (
            <span className="text-xs text-muted-foreground">
              Data: {forecastInfo.date_range_start?.slice(0, 10)} to {forecastInfo.date_range_end?.slice(0, 10)}
            </span>
          )}
        </div>

        {/* Stock Update Dates */}
        {stockUpdateDates && (
          <div className="border rounded-sm p-3 bg-blue-50 border-blue-200" data-testid="stock-update-info">
            <div className="flex flex-wrap gap-4 text-sm text-blue-800">
              {stockUpdateDates.central_warehouse && (
                <span>
                  <span className="font-medium">📦 Central Warehouse:</span>{' '}
                  {new Date(stockUpdateDates.central_warehouse).toLocaleDateString('en-IN', { 
                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              )}
              {stockUpdateDates.fba_warehouses?.[currentPlatform] && (
                <span>
                  <span className="font-medium">🏭 FBA Stock ({selectedCountry}):</span>{' '}
                  {new Date(stockUpdateDates.fba_warehouses[currentPlatform]).toLocaleDateString('en-IN', { 
                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Unmatched SKU Warning */}
        {forecastInfo?.unmatched_sku_count > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-3 flex items-center justify-between" data-testid="unmatched-sku-warning">
            <p className="text-sm text-orange-700">
              <span className="font-medium">⚠️ {forecastInfo.unmatched_sku_count} SKUs</span> in warehouse stock don&apos;t have matching products in inventory. 
              Add these SKUs to your product catalog to include them in forecasting.
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={downloadUnmatchedSkus}
              className="ml-4 shrink-0 border-orange-500/50 text-orange-700 hover:bg-orange-500/10"
              data-testid="download-unmatched-skus"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Download List
            </Button>
          </div>
        )}

        {!forecastInfo?.has_data && forecastInfo && (
          <div className="bg-warning/10 border border-warning/30 rounded-sm p-4" data-testid="forecast-info">
            <p className="text-sm">
              <span className="font-medium">⚠️ Warning: </span>
              {forecastInfo.message}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading forecast...</p>
          </div>
        ) : (
          <div className="border border-border rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="forecast-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">SKU</th>
                    <th className="text-left p-3 text-xs uppercase tracking-widest font-medium">Product</th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">
                      <span className="text-blue-500">Warehouse Stock</span>
                    </th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">
                      <span className="text-blue-500">In Transit</span>
                    </th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Central Stock</th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">
                      <span className="text-green-600">Adjusted Stock</span>
                    </th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Velocity/Day</th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Order Qty</th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">
                      <span className="text-purple-600">Allocated</span>
                    </th>
                    <th className="text-right p-3 text-xs uppercase tracking-widest font-medium">Cover (Days)</th>
                    <th className="text-center p-3 text-xs uppercase tracking-widest font-medium">Status</th>
                    <th className="text-center p-3 text-xs uppercase tracking-widest font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedForecasts.length > 0 ? (
                    paginatedForecasts.map((forecast) => (
                      <tr 
                        key={forecast.sku} 
                        data-testid={`forecast-row-${forecast.sku}`} 
                        className={`border-b border-border hover:bg-muted/30 transition-colors ${forecast.has_shortage ? 'bg-orange-500/5' : ''}`}
                      >
                        <td className="p-3 font-mono text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {forecast.sku}
                            {forecast.consider_discontinuing && (
                              <span className="text-xs text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded flex items-center gap-0.5" title="Consider Discontinuing - Low velocity">
                                <TrendingDown className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-sm max-w-[200px] truncate">{forecast.product_name}</td>
                        <td className="p-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            value={editingStock[forecast.sku]?.warehouse_stock ?? forecast.warehouse_stock}
                            onChange={(e) => handleStockChange(forecast.sku, 'warehouse_stock', e.target.value)}
                            className="w-20 h-8 text-right font-mono text-sm inline-block"
                            data-testid={`input-warehouse-stock-${forecast.sku}`}
                          />
                        </td>
                        <td className="p-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            value={editingStock[forecast.sku]?.stock_in_transit ?? forecast.stock_in_transit}
                            onChange={(e) => handleStockChange(forecast.sku, 'stock_in_transit', e.target.value)}
                            className="w-20 h-8 text-right font-mono text-sm inline-block"
                            data-testid={`input-in-transit-${forecast.sku}`}
                          />
                        </td>
                        <td className="p-3 text-right font-mono text-sm">{forecast.central_stock}</td>
                        <td className="p-3 text-right font-mono text-sm">
                          <span className={`font-medium ${forecast.adjusted_stock < forecast.central_stock ? 'text-orange-600' : 'text-green-600'}`}>
                            {forecast.adjusted_stock}
                          </span>
                          {forecast.allocation_note && (
                            <div className="text-xs text-muted-foreground truncate max-w-[120px]" title={forecast.allocation_note}>
                              {forecast.allocation_note}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono text-sm">
                          {forecast.sales_velocity}
                          {forecast.is_combo && (
                            <span className="ml-1 text-xs text-purple-500" title="Combo product">📦</span>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono text-sm font-medium">
                          <span className="inline-flex items-center gap-1 justify-end">
                            {forecast.order_quantity}
                            {forecast.order_quantity === 0 && forecast.min2_reason && (
                              <Info
                                className="h-3.5 w-3.5 text-blue-500 cursor-help shrink-0"
                                title={forecast.min2_reason}
                                data-testid={`min2-reason-${forecast.sku}`}
                              />
                            )}
                          </span>
                          {forecast.has_shortage && (
                            <div className="text-xs text-orange-600">
                              Short: {forecast.shortage}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right font-mono text-sm">
                          <span className={`font-medium ${forecast.allocated_quantity > 0 ? 'text-purple-600' : 'text-muted-foreground'}`}>
                            {forecast.allocated_quantity}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono text-sm">
                          <span className={forecast.stock_cover_days < currentSettings.shipping_lead_time_days ? 'text-destructive font-medium' : ''}>
                            {forecast.stock_cover_days}
                          </span>
                        </td>
                        <td className="p-3 text-center">{getStatusBadge(forecast.status, forecast.has_shortage)}</td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => saveStock(forecast.sku)}
                            disabled={savingStock[forecast.sku]}
                            data-testid={`save-stock-${forecast.sku}`}
                          >
                            {savingStock[forecast.sku] ? (
                              <span className="animate-spin">⏳</span>
                            ) : (
                              <Save className="w-4 h-4" strokeWidth={1.5} />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="p-12 text-center">
                        <p className="text-muted-foreground" data-testid="no-forecast-message">
                          {searchTerm 
                            ? `No products found matching "${searchTerm}"`
                            : `No forecast data available for ${selectedCountry}. Upload sales data or warehouse stock.`
                          }
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    data-testid="pagination-first"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-8 h-8 p-0"
                          data-testid={`pagination-page-${pageNum}`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-last"
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        {forecasts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="border border-border bg-card rounded-sm p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Products</p>
              <p className="text-2xl font-bold font-mono" data-testid="total-products-count">{forecasts.length}</p>
            </div>
            <div className="border border-border bg-card rounded-sm p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">With Sales Data</p>
              <p className="text-2xl font-bold font-mono" data-testid="with-sales-count">
                {forecasts.filter((f) => f.sales_velocity > 0).length}
              </p>
            </div>
            <div className="border border-border bg-card rounded-sm p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Urgent Actions</p>
              <p className="text-2xl font-bold font-mono text-destructive" data-testid="urgent-count">
                {forecasts.filter((f) => f.status === 'urgent').length}
              </p>
            </div>
            <div className="border border-border bg-card rounded-sm p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Stock Shortages</p>
              <p className="text-2xl font-bold font-mono text-orange-600" data-testid="shortage-count">
                {forecasts.filter((f) => f.has_shortage).length}
              </p>
            </div>
            <div className="border border-border bg-card rounded-sm p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Order Qty</p>
              <p className="text-2xl font-bold font-mono" data-testid="total-order-qty">
                {forecasts.reduce((sum, f) => sum + f.order_quantity, 0).toLocaleString()} units
              </p>
            </div>
          </div>
        )}

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Update Warehouse Stock</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload a CSV or Excel file to bulk update stock for <strong>{countries.find(c => c.code === selectedCountry)?.name}</strong> warehouse.
              </p>
              
              <div className="bg-muted/30 border border-border rounded-sm p-4">
                <p className="text-sm font-medium mb-2">Required Columns:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>SKU</strong> - Product SKU (channel or master)</li>
                  <li>• <strong>Current Stock</strong> - Units at warehouse</li>
                  <li>• <strong>Stock in Transit</strong> - Units being shipped (optional)</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3 pt-2 border-t border-border">
                  Channel SKUs are automatically mapped to master SKUs using your SKU mappings.
                </p>
              </div>

              <Button variant="outline" onClick={downloadTemplate} className="w-full" data-testid="download-stock-template">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Download Template
              </Button>

              <div>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBulkUpload}
                  disabled={uploading}
                  data-testid="stock-upload-input"
                />
              </div>

              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm">Uploading...</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Warehouse Settings - {countries.find(c => c.code === selectedCountry)?.name}</DialogTitle>
            </DialogHeader>
            <SettingsForm 
              settings={currentSettings} 
              onSave={saveWarehouseSettings}
              onCancel={() => setSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function SettingsForm({ settings, onSave, onCancel }) {
  const [leadTime, setLeadTime] = useState(settings.shipping_lead_time_days || 14);
  const [targetDays, setTargetDays] = useState(settings.target_days_of_supply || 30);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      shipping_lead_time_days: parseInt(leadTime),
      target_days_of_supply: parseInt(targetDays)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Shipping Lead Time (days)</label>
        <Input
          type="number"
          min="1"
          value={leadTime}
          onChange={(e) => setLeadTime(e.target.value)}
          data-testid="input-lead-time"
        />
        <p className="text-xs text-muted-foreground mt-1">Time to ship from central warehouse to this FBA</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">Target Days of Supply</label>
        <Input
          type="number"
          min="1"
          value={targetDays}
          onChange={(e) => setTargetDays(e.target.value)}
          data-testid="input-target-days"
        />
        <p className="text-xs text-muted-foreground mt-1">How many days of stock to maintain at warehouse</p>
      </div>

      <div className="bg-muted/30 border border-border rounded-sm p-3">
        <p className="text-sm">
          <strong>Formula:</strong> Order Qty = Velocity × ({leadTime} + {targetDays}) - Stock - Transit
        </p>
      </div>

      <div className="flex gap-3">
        <Button type="submit" data-testid="save-settings-button">Save Settings</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
